import { formatLocalIsoDate } from '../../../../shared/utils/ship-from-local-clock';
import type { PaymentCardBrand } from './create-shipment.model';

export type { PaymentCardBrand } from './create-shipment.model';

/**
 * Completed-shipment confirmation payload for the Get Labels page (OM-66 / OM-69).
 * Populated from the Shipment Orchestration Lambda once available; until then
 * tests and local seeding use fixtures under `mocks/`.
 */
export interface CompletedShipmentPaymentDisplay {
  /** Card network shown on Estimated Costs / print (Visa, Mastercard, AMEX). */
  cardBrand?: PaymentCardBrand;
  /** Masked card number shown on Estimated Costs (e.g. *******2088). */
  maskedPan: string;
  /** Card expiry as MM/YYYY for the Payment Method display. */
  expiry: string;
  /** Billing contact name. */
  billingName: string;
  /** Billing postal / ZIP code. */
  billingPostalCode: string;
}

/** Backend-generated document descriptor returned by Shipment Lambda document generation. */
export interface CompletedShipmentDocumentDisplay {
  documentId: string;
  docType?: string;
  status?: string;
  docURL?: string | null;
  failureMessage?: string | null;
}

/** Display projection returned by Shipment Lambda SUCCESS for completed-shipment details. */
export interface ShipmentInformationEstimate {
  currency: string;
  shipmentCharge: number;
  fuelSurchargeAmount: number;
  totalCharge: number;
  gstTaxAmount: number;
  pstTaxAmount: number;
  hstTaxAmount: number;
  qstTaxAmount: number;
  /** Peak / multi-piece / holiday amounts from SUCCESS estimate pricing (OM-735). */
  surcharges?: {
    peak: number;
    multiPiece: number;
    holiday: number;
  };
  /** Per-SH peak breakdown for Estimated Costs Peak Large / Peak Oversize (OM-724). */
  peakSurcharges?: { productCode: string; amount: number }[];
  /** Rated accessorial lines (SH, LDD, Beyond, SIG, COV) for Get Labels / print. */
  accessorialCharges?: { productCode: string; amount: number }[];
}

/** Shipment Lambda address projection for completed-shipment details. */
export interface ShipmentInformationAddress {
  qualifier: 'SH' | 'CN' | 'LB';
  companyName?: string;
  attentionToName?: string;
  department?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  streetAddress3?: string;
  /** Unit/suite/apartment identifier from SUCCESS (OM-763). */
  suiteAptUnit?: string;
  city: string;
  provinceState: string;
  postalZipCode: string;
  countryCode: string;
  residential?: boolean;
  phoneNumber?: string;
  email?: string;
  extension?: string;
  /** Entry/buzzer code from SUCCESS (OM-763). */
  entryBuzzerCode?: string;
}

/** Rated service name projected for Get Labels / print. */
export interface ShipmentInformationServiceDisplay {
  productCode: string;
  displayName: string;
}

/** Packaging summary projected by the Shipment Lambda for the confirmation page. */
export interface ShipmentInformationPackaging {
  packageTypeDescription: string;
  totalPieces: number;
  totalWeight: number;
  weightUnit?: 'KG' | 'LB';
  specialHandling: boolean;
  additionalHandling: boolean;
  specialHandlingTypes?: { code: string; displayName: string }[];
  additionalHandlingCount?: number;
}

/** Declared-value / signature summary projected by the Shipment Lambda. */
export interface ShipmentInformationAdditionalServices {
  declaredValue: number | null;
  signatureRequired: boolean;
  signatureType?: 'REQUIRED' | 'ADULT' | null;
  /** Package type description; Get Labels additional-services card does not display this. */
  packageDescription?: string;
}

/** Piece projection bound to a generated tracking PIN. */
export interface ShipmentInformationItem {
  lineNumber: number;
  pin: string;
  services: unknown[];
}

/** Shipment Lambda SUCCESS shipmentInformation used by view/download details. */
export interface CompletedShipmentInformation {
  shipmentPin: string;
  piecePins?: string[];
  shipmentDate: string;
  expectedDeliveryDate: string;
  productCode: string;
  service?: ShipmentInformationServiceDisplay;
  billingMethod: 'CREDIT_CARD' | 'ACCOUNT_BILLED';
  billingAccountNumber?: string;
  language: 'en' | 'fr';
  addresses: {
    shipFrom: ShipmentInformationAddress;
    shipTo: ShipmentInformationAddress;
    returnTo: ShipmentInformationAddress | null;
  };
  estimate: ShipmentInformationEstimate;
  items?: ShipmentInformationItem[];
  services?: unknown[];
  references?: Record<string, string>;
  notifications?: {
    language: 'en' | 'fr';
    senderReceiveUpdates: boolean;
    recipientReceiveUpdates: boolean;
  };
  packaging?: ShipmentInformationPackaging;
  additionalServices?: ShipmentInformationAdditionalServices;
  pickupType?: 'DROPOFF' | 'PRESCHEDULED';
  pickup?: {
    pickupId?: string;
    confirmationNumber?: string;
    status: string;
    pickupDate?: string;
    readyTime?: string;
    closeTime?: string;
    locationCode?: string;
    failureMessage?: string;
  };
}

export interface CompletedShipmentConfirmation {
  /** Primary customer-facing shipment reference (tracking PIN). */
  trackingPin: string;
  /** ISO date (yyyy-MM-dd) for the customer-facing Shipment Creation Date. */
  shipmentCreationDate: string;
  /** Local cancel deadline (ISO datetime) for the customer-facing cancel copy. */
  cancelDeadlineLocal: string;
  /** Whether confirmation email was sent. */
  confirmationEmailSent: boolean;
  /** CSOP shipment id when returned from create SUCCESS (OM-69 cancel). */
  shipmentId?: string;
  /** Pickup confirmation number when a courier pickup was scheduled. */
  pickupConfirmationNumber?: string;
  /** True after a successful POST /shipments/cancel. */
  cancelled?: boolean;
  /** ISO timestamp when the shipment was cancelled. */
  cancelledAt?: string;
  /** Payment Method / Billing Address display for Estimated Costs. */
  paymentDisplay?: CompletedShipmentPaymentDisplay;
  /** Authoritative Shipment Lambda SUCCESS details for view/download. */
  shipmentInformation?: CompletedShipmentInformation;
  /** Printable shipping label (DomesticBillOfLading) backing the View/Download action. */
  labelDocument?: CompletedShipmentDocumentDisplay;
  /** Shipment Details summary PDF; its docType is still pending Shipment Lambda confirmation. */
  shipmentDetailsDocument?: CompletedShipmentDocumentDisplay;
}

/**
 * Returns true when the payload has a non-empty tracking PIN required to show Get Labels.
 */
export function isValidCompletedShipment(
  value: CompletedShipmentConfirmation | null | undefined,
): value is CompletedShipmentConfirmation {
  return Boolean(value?.trackingPin?.trim());
}

/**
 * Reads the Ship From province/territory code used for cancel local-time (OM-891).
 */
export function readShipFromProvinceCode(
  confirmation: CompletedShipmentConfirmation | null | undefined,
): string | null {
  const province = confirmation?.shipmentInformation?.addresses?.shipFrom?.provinceState?.trim();
  return province || null;
}

/**
 * Formats the Ship From civil calendar date as YYYY-MM-DD for cancel eligibility (OM-891).
 * Falls back to America/Toronto when province is unknown (same default as pickup).
 */
export function formatShipFromLocalDate(
  confirmation: CompletedShipmentConfirmation | null | undefined,
  now: Date = new Date(),
): string {
  return formatLocalIsoDate(now, { provinceCode: readShipFromProvinceCode(confirmation) });
}

/**
 * Returns true when cancel is still available for the completed shipment (OM-69 / OM-891).
 * Same calendar day as `shipmentCreationDate` in the Ship From province timezone — not the browser TZ.
 */
export function isCancelShipmentAvailable(
  confirmation: CompletedShipmentConfirmation,
  now: Date = new Date(),
): boolean {
  if (confirmation.cancelled) {
    return false;
  }

  const creationDate = confirmation.shipmentCreationDate?.trim();
  if (!creationDate) {
    return false;
  }

  return formatShipFromLocalDate(confirmation, now) === creationDate;
}

/**
 * Formats the browser's local calendar date as YYYY-MM-DD.
 * Prefer {@link formatShipFromLocalDate} for cancel eligibility (OM-891).
 */
export function formatBrowserLocalDate(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
